import React, { useEffect, useRef, useState } from 'react';
import { MdPhotoCamera, MdSave } from 'react-icons/md';
import { maskCnpj } from '../../Auxiliar/Masks';
import Attention from '../ReusableComponents/Attention';
import DefaultButton from '../ReusableComponents/DefaultButton';
import PasswordInput from '../ReusableComponents/PasswordInput';
import PortalColors from './PortalColors';
import OrganizationLogo from './OrganizationLogo';
import OrganizationAddressFields from './OrganizationAddressFields';
import useOrganizationAddress from './useOrganizationAddress';
import { useSaveOrganization } from './hooks';
import { usePortal } from '../PortalAluno/hooks';
import { ErrorNotice } from '../PortalAluno/components/Feedback';
export default function InstitutionForm({ organization: o, organizationId, onSaved, backTo }) {
  const { user } = usePortal();
  const canConfigure = user?.role === 'region_admin' && o.can_enable_portal;
  const address = useOrganizationAddress(o.endereco);
  const save = useSaveOrganization(organizationId),
    [removeLogo, setRemoveLogo] = useState(false),
    [file, setFile] = useState(null),
    [preview, setPreview] = useState(''),
    [logoError, setLogoError] = useState('');
  const logoInput = useRef(null);
  const [password, setPassword] = useState(''),
    [confirmation, setConfirmation] = useState('');
  useEffect(() => {
    if (!file) {
      setPreview('');
      return;
    }
    const image = URL.createObjectURL(file);
    setPreview(image);
    return () => URL.revokeObjectURL(image);
  }, [file]);
  const fields = [
    ['name', 'Nome da organizadora', true],
    ['cnpj', 'CNPJ', true],
    ['razao_social', 'Razão social'],
    ['nome_fantasia', 'Nome fantasia'],
    ['email_institucional', 'E-mail institucional'],
    ['telefone', 'Telefone'],
  ];
  return (
    <form
      className="portal-admin-form hidethis"
      onSubmit={(event) => {
        event.preventDefault();
        if (address.isFetching || save.isLoading) return;
        const data = new FormData(event.currentTarget);
        if (!file) data.delete('logo');
        data.set('remove_logo', removeLogo ? '1' : '0');
        if (canConfigure) {
          data.set('portal_enabled', data.has('portal_enabled') ? '1' : '0');
          data.set('change_text', data.has('change_text') ? '1' : '0');
        } else {
          data.delete('portal_enabled');
          data.delete('change_text');
        }
        if (o.can_edit_owner && password) {
          data.set('owner[password]', password);
          data.set('owner[password_confirmation]', confirmation);
        }
        save.mutate(data, { onSuccess: onSaved });
      }}
    >
      <p className="form-text mt-0">
        Os campos com <Attention /> são obrigatórios. Os demais são opcionais.
      </p>
      <fieldset disabled={!o.can_edit || save.isLoading}>
        <div className="organization-logo-editor">
          <button
            type="button"
            className="organization-logo-picker"
            aria-label="Alterar logo da organizadora"
            aria-describedby="organization-logo-help"
            onClick={() => logoInput.current?.click()}
          >
            <OrganizationLogo
              src={removeLogo ? null : preview || o.logo_url}
              alt="Prévia do logo da organizadora"
            />
            <span className="organization-logo-edit-icon" aria-hidden="true">
              <MdPhotoCamera />
            </span>
          </button>
          <div className="organization-logo-controls">
            <label className="form-label" htmlFor="organization-logo-file">
              <span>
                Logo da organizadora <small className="text-muted">(opcional)</small>
              </span>
            </label>
            <input
              ref={logoInput}
              id="organization-logo-file"
              className="visually-hidden"
              type="file"
              name="logo"
              accept="image/png,image/jpeg,image/webp"
              aria-label="Logo da organizadora"
              aria-describedby="organization-logo-help"
              onChange={(e) => {
                const upload = e.target.files?.[0];
                const error =
                  upload && upload.size > 2 * 1024 * 1024 ? 'Envie um logo de até 2 MB.' : '';
                e.target.setCustomValidity(error);
                setLogoError(error);
                setFile(error ? null : upload || null);
                if (upload && !error) setRemoveLogo(false);
              }}
            />
            <p id="organization-logo-help" className="form-text mb-0">
              Clique no logo para alterar. PNG, JPG ou WebP, até 2 MB.
            </p>
            {logoError && (
              <p className="text-danger mb-0" role="alert">
                {logoError}
              </p>
            )}
            {o.logo_url && (
              <label className="portal-check organization-logo-remove">
                <input
                  type="checkbox"
                  checked={removeLogo}
                  onChange={(e) => {
                    setRemoveLogo(e.target.checked);
                    if (e.target.checked) {
                      setFile(null);
                      setLogoError('');
                      if (logoInput.current) {
                        logoInput.current.value = '';
                        logoInput.current.setCustomValidity('');
                      }
                    }
                  }}
                />{' '}
                Remover o logo atual
              </label>
            )}
          </div>
        </div>
        <div className="portal-admin-grid">
          {fields.map(([name, label, required]) => (
            <label className="form-label" key={name}>
              <span>
                {label}{' '}
                {required ? <Attention /> : <small className="text-muted">(opcional)</small>}
              </span>
              <input
                className="form-control"
                name={name}
                type={
                  name === 'email_institucional' ? 'email' : name === 'telefone' ? 'tel' : 'text'
                }
                required={required}
                maxLength={name === 'cnpj' ? 30 : name === 'telefone' ? 40 : 255}
                defaultValue={o[name] || ''}
                onChange={
                  name === 'cnpj'
                    ? (e) => {
                        e.target.value = maskCnpj(e.target.value);
                      }
                    : undefined
                }
              />
            </label>
          ))}
        </div>
        <label className="form-label">
          <span>
            Descrição <small className="text-muted">(opcional)</small>
          </span>
          <textarea
            className="form-control"
            name="descricao"
            rows={4}
            maxLength={20000}
            defaultValue={o.descricao || ''}
          />
        </label>
        <OrganizationAddressFields {...address} />
        {canConfigure && (
          <div className="portal-form-section">
            <h3>Configurações da organizadora</h3>
            <label className="portal-check">
              <input type="checkbox" name="portal_enabled" defaultChecked={o.portal_enabled} />{' '}
              Habilitar o portal de inscrições
            </label>
            <p className="form-text">
              Permite configurar e publicar as inscrições dos seletivos desta organizadora.
            </p>
            <label className="portal-check">
              <input type="checkbox" name="change_text" defaultChecked={o.change_text} /> Usar a
              nomenclatura de seletivos e vagas/cargos
            </label>
          </div>
        )}
        {o.can_customize_portal && <PortalColors value={o.portal_colors} />}
        {o.can_edit_owner && (
          <div className="portal-form-section">
            <h3>Responsável pela organizadora</h3>
            <div className="portal-admin-grid">
              <label className="form-label">
                <span>
                  Nome do responsável <Attention />
                </span>
                <input
                  className="form-control"
                  name="owner[name]"
                  required
                  maxLength={255}
                  defaultValue={o.owner?.name || ''}
                />
              </label>
              <label className="form-label">
                <span>
                  E-mail de acesso <Attention />
                </span>
                <input
                  className="form-control"
                  type="email"
                  name="owner[email]"
                  required
                  maxLength={255}
                  defaultValue={o.owner?.email || ''}
                />
              </label>
            </div>
            <p className="form-text">
              Preencha uma nova senha apenas se quiser alterar o acesso do responsável.
            </p>
            <div className="portal-admin-grid">
              <PasswordInput
                label="Nova senha (opcional)"
                placeholder="Manter a senha atual"
                value={password}
                OnChangeText={(e) => setPassword(e.target.value)}
              />
              <PasswordInput
                label="Confirmar nova senha"
                value={confirmation}
                required={password ? true : undefined}
                OnChangeText={(e) => setConfirmation(e.target.value)}
              />
            </div>
          </div>
        )}
        <ErrorNotice error={save.error} />
        <div className="portal-form-footer">
          <DefaultButton text="Voltar" bg="secondary" to={backTo} disabled={save.isLoading} />
          <DefaultButton
            type="submit"
            text="Salvar alterações"
            bg="confirm"
            icon={<MdSave />}
            loading={save.isLoading}
            disabled={address.isFetching}
            loadingtext="Salvando alterações"
          />
        </div>
      </fieldset>
    </form>
  );
}
